import React, { useState, useEffect } from 'react';
import {
  Search,
  Zap,
  ShieldCheck,
  CheckCircle2,
  Building2,
  Phone,
  Mail,
  User,
  ArrowRight,
  ExternalLink,
  ChevronLeft,
  RotateCcw,
  Sparkles,
  FileText,
  X,
  Cpu,
  MapPin,
  AlertTriangle,
  Layers,
  Map as MapIcon,
  Compass
} from 'lucide-react';
import { queryKepcoGridCapacity, type KepcoGridResult } from '../services/kepcoApi';

interface GridCalculatorPageProps {
  onBackToHome: () => void;
}

declare global {
  interface Window {
    daum?: {
      Postcode: new (options: {
        oncomplete: (data: {
          address: string;
          zonecode: string;
          bname: string;
          buildingName: string;
          sido: string;
          sigungu: string;
        }) => void;
      }) => {
        open: () => void;
      };
    };
  }
}

export const GridCalculatorPage: React.FC<GridCalculatorPageProps> = ({ onBackToHome }) => {
  // Form States
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    zonecode: '',
    address: '',
    detailAddress: '',
    pyeong: 200,
    buildingUse: '공장 지붕',
    privacyAgreed: true,
    marketingAgreed: true,
  });

  const [analyzing, setAnalyzing] = useState(false);
  const [analysisStep, setAnalysisStep] = useState(0);
  const [analyzed, setAnalyzed] = useState(false);
  const [kepcoData, setKepcoData] = useState<KepcoGridResult | null>(null);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [mapViewMode, setMapViewMode] = useState<'satellite' | 'roadmap'>('satellite');

  // Scroll to top when page opens
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  // Daum Postcode address search popup handler
  const handleOpenAddressSearch = () => {
    if (window.daum && window.daum.Postcode) {
      new window.daum.Postcode({
        oncomplete: (data) => {
          setFormData((prev) => ({
            ...prev,
            zonecode: data.zonecode,
            address: data.address,
          }));
        },
      }).open();
    } else {
      alert('주소 검색 서비스를 불러오는 중입니다. 잠시 후 다시 시도해 주세요.');
    }
  };

  // Phone auto formatting
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^0-9]/g, '');
    let formatted = raw;
    if (raw.length > 3 && raw.length <= 7) {
      formatted = `${raw.slice(0, 3)}-${raw.slice(3)}`;
    } else if (raw.length > 7) {
      formatted = `${raw.slice(0, 3)}-${raw.slice(3, 7)}-${raw.slice(7, 11)}`;
    }
    setFormData((prev) => ({ ...prev, phone: formatted }));
  };

  // =================== RENTAL & CAPACITY METRICS ===================
  const pyeongNum = Math.max(10, Number(formData.pyeong) || 0);
  const areaSqm = Math.round(pyeongNum * 3.305785);
  
  // 2:1 ratio (2평당 1kW -> 0.5 kW/평, 200평 = 100 kW, 2,000평 = 1,000 kW)
  const estimatedKw = Math.round(pyeongNum * 0.5);

  // 1kW당 연간 50,000원 기준 (200평/100kW 기준: 연 500만 원 / 20년 총 1억 원)
  const estimatedAnnualRentWon = Math.round(estimatedKw * 50000);
  const estimatedAnnualRentFormatted =
    estimatedAnnualRentWon >= 100000000
      ? `${(estimatedAnnualRentWon / 100000000).toFixed(1)}억 원`
      : `${Math.round(estimatedAnnualRentWon / 10000).toLocaleString()}만 원`;

  const estimatedTotalRentWon = estimatedAnnualRentWon * 20;
  const estimatedTotalRentFormatted =
    estimatedTotalRentWon >= 100000000
      ? `${(estimatedTotalRentWon / 100000000).toFixed(1)}억 원`
      : `${Math.round(estimatedTotalRentWon / 10000).toLocaleString()}만 원`;

  // Clean address string for map geocoding queries
  const cleanAddress = formData.address.replace(/\s*\([^)]*\)/g, '').trim();

  // Form submit & analysis simulation with KEPCO Grid Capacity Engine
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      alert('성함이나 업체명을 입력해 주세요.');
      return;
    }
    if (!formData.address.trim()) {
      alert('주소 검색 버튼을 눌러 설치 대상 주소를 선택해 주세요.');
      handleOpenAddressSearch();
      return;
    }
    if (!formData.phone || formData.phone.length < 10) {
      alert('올바른 연락처(휴대폰 번호)를 입력해 주세요.');
      return;
    }
    if (!formData.email.trim() || !formData.email.includes('@')) {
      alert('분석 리포트를 수신하실 올바른 이메일 주소를 입력해 주세요.');
      return;
    }
    if (!formData.privacyAgreed || !formData.marketingAgreed) {
      alert('개인정보 수집 및 상담 수신에 동의해 주세요.');
      return;
    }

    setAnalyzing(true);
    setAnalysisStep(1);

    // Call KEPCO Grid Capacity Engine
    const gridResult = await queryKepcoGridCapacity(
      formData.address,
      undefined,
      formData.pyeong
    );
    setKepcoData(gridResult);

    // Google Sheets endpoint submission
    const GOOGLE_SCRIPT_URL =
      'https://script.google.com/macros/s/AKfycbyuQ7ObF2-CirZKOUyZLjjpbI7coroPEYMXcSmFgOsnyequ4_ViHfP-DOXSlA83dPkC/exec';

    try {
      const fullAddress = `${formData.address} ${formData.detailAddress}`.trim();
      const params = new URLSearchParams();
      params.append('sheetType', '전기확인시트');
      params.append('name', formData.name);
      params.append('phone', formData.phone);
      params.append('email', formData.email);
      params.append('address', fullAddress);
      params.append('zonecode', formData.zonecode || '');
      params.append('pyeong', formData.pyeong.toString());
      params.append('buildingUse', formData.buildingUse);
      params.append('estimatedKw', `${estimatedKw} kW`);
      params.append('estimatedAnnualIncome', `연간 ${estimatedAnnualRentFormatted}`);
      params.append('estimatedIncome', `20년 총 ${estimatedTotalRentFormatted}`);
      params.append('repairSupport', '지붕 수리비 지원 적용');
      params.append('kepcoBranch', gridResult.branchName);
      params.append('kepcoSubstation', `${gridResult.substationName} (잔여 ${gridResult.substationRemainingMw}MW)`);
      params.append('kepcoDl', `${gridResult.dlName} (잔여 ${gridResult.dlRemainingKw.toLocaleString()}kW)`);
      params.append('gridStatus', gridResult.gridGradeText);
      params.append('verifyCode', gridResult.verificationCode);
      params.append('privacyAgreed', formData.privacyAgreed ? '동의' : '미동의');
      params.append('marketingAgreed', formData.marketingAgreed ? '동의' : '미동의');
      params.append('createdAt', new Date().toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' }));

      fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
      }).catch((err) => console.error('Sheet logging error:', err));
    } catch (err) {
      console.error('Submission error:', err);
    }

    // Step-by-step radar scan animation
    setTimeout(() => setAnalysisStep(2), 700);
    setTimeout(() => setAnalysisStep(3), 1500);
    setTimeout(() => {
      setAnalyzing(false);
      setAnalyzed(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 2200);
  };

  const handleReset = () => {
    setAnalyzed(false);
    setAnalyzing(false);
    setAnalysisStep(0);
    setKepcoData(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const buildingUseOptions = [
    '공장 지붕',
    '축사 / 돈사',
    '창고 / 물류센터',
    '일반 상가 / 빌딩',
    '토지 / 나대지',
    '기타 유휴공간',
  ];

  const pyeongPresets = [100, 200, 300, 500, 1000, 2000];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans selection:bg-sky-500 selection:text-white">
      {/* Top Fixed Header (Clean White Mode) */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-200 py-3.5 px-4 sm:px-6 shadow-sm">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <button
            onClick={onBackToHome}
            className="flex items-center gap-2 text-xs sm:text-sm font-extrabold text-slate-700 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 border border-slate-200 px-3.5 py-2 rounded-xl transition-all cursor-pointer shadow-sm"
          >
            <ChevronLeft className="w-4 h-4 text-sky-600" />
            <span>메인 홈으로 돌아가기</span>
          </button>

          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-lg bg-white p-0.5 shadow-sm border border-slate-200 flex items-center justify-center overflow-hidden shrink-0">
              <img src="/plus_sd_logo.png" alt="플러스에스디" className="w-full h-full object-contain" />
            </div>
            <span className="text-xs sm:text-sm font-black text-slate-900 tracking-tight hidden sm:inline">
              실시간 수익 계산기
            </span>
          </div>

          <a
            href="tel:01027671449"
            className="flex items-center gap-1.5 text-xs font-black text-sky-600 bg-sky-50 border border-sky-200 px-3.5 py-1.5 rounded-xl hover:bg-sky-100 transition-colors shadow-sm"
          >
            <Phone className="w-3.5 h-3.5" />
            <span>010-2767-1449</span>
          </a>
        </div>
      </header>

      {/* Main Container */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        
        {/* Analyzing Radar Overlay (White Light Theme) */}
        {analyzing && (
          <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-white/95 backdrop-blur-xl px-4 text-center animate-in fade-in duration-200">
            <div className="relative mb-8">
              {/* Radar concentric circles */}
              <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-full border-2 border-sky-300 animate-ping absolute inset-0" />
              <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-full border-2 border-sky-400 animate-pulse flex items-center justify-center bg-sky-50 shadow-lg shadow-sky-500/20">
                <Zap className="w-12 h-12 sm:w-16 sm:h-16 text-sky-600 animate-bounce" />
              </div>
            </div>

            <h3 className="text-xl sm:text-2xl font-black text-slate-900 mb-2">
              한전 배전계통 실시간 여유용량 정밀 분석 중...
            </h3>
            <p className="text-xs text-slate-500 mb-5 font-medium">
              관할 154kV 변전소 및 22.9kV 배전선로(D/L) 전력망 대조 분석
            </p>

            <div className="space-y-2.5 max-w-md mx-auto text-xs sm:text-sm text-slate-700 font-semibold text-left">
              <p className={`transition-all duration-300 ${analysisStep >= 1 ? 'text-sky-600 font-black' : 'text-slate-400'}`}>
                ✓ 154kV 관할 변전소 M.Tr 잔여 여유용량 전산 대조
              </p>
              <p className={`transition-all duration-300 ${analysisStep >= 2 ? 'text-sky-600 font-black' : 'text-slate-400'}`}>
                ✓ 배전선로(D/L) 송전 연계 허용용량 및 포화 여부 검증
              </p>
              <p className={`transition-all duration-300 ${analysisStep >= 3 ? 'text-sky-600 font-black' : 'text-slate-400'}`}>
                ✓ {formData.pyeong}평 기준 지붕 수리비 지원 및 20년 임대수익 산정
              </p>
            </div>
          </div>
        )}

        {/* ===================== VIEW 1: RESULT REPORT (WHITE THEME) ===================== */}
        {analyzed && kepcoData ? (
          <div className="space-y-8 animate-in fade-in zoom-in-95 duration-300">
            
            {/* Result Header Badge */}
            <div className="text-center space-y-3">
              <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs sm:text-sm font-extrabold border ${
                kepcoData.isSaturated
                  ? 'bg-amber-50 border-amber-300 text-amber-700 shadow-sm'
                  : 'bg-emerald-50 border-emerald-300 text-emerald-700 shadow-sm'
              }`}>
                {kepcoData.isSaturated ? (
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                )}
                <span>{kepcoData.gridGradeBadge}</span>
              </div>

              {/* Title: "김동현 대표님 분석 리포트" */}
              <h1 className="text-2xl sm:text-4xl font-black text-slate-900 tracking-tight leading-snug">
                <span className="text-sky-600">{formData.name}</span> 대표님 분석 리포트
              </h1>
              
              <div className="flex flex-wrap items-center justify-center gap-2 text-xs text-slate-500 font-medium">
                <span>조회 주소: <strong className="text-slate-800">{formData.address} {formData.detailAddress}</strong></span>
                <span>•</span>
                <span>검증번호: <span className="font-mono text-sky-600 font-bold">{kepcoData.verificationCode}</span></span>
                <span>•</span>
                <span>조회일시: <span className="text-slate-600">{kepcoData.inquiryTime}</span></span>
              </div>
            </div>

            {/* Core Summary Metric Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              
              {/* Card 1: Grid Status */}
              <div className={`bg-white border rounded-2xl sm:rounded-3xl p-5 sm:p-6 shadow-md relative overflow-hidden ${
                kepcoData.isSaturated ? 'border-amber-300 bg-amber-50/30' : 'border-emerald-300 bg-emerald-50/30'
              }`}>
                <span className={`text-xs font-extrabold block mb-1 ${
                  kepcoData.isSaturated ? 'text-amber-700' : 'text-emerald-700'
                }`}>
                  한전 계통 현황 판정
                </span>
                <h3 className={`text-xl sm:text-2xl font-black mb-1.5 ${
                  kepcoData.isSaturated ? 'text-amber-600' : 'text-emerald-600'
                }`}>
                  {kepcoData.isSaturated ? '일반 송전선로 포화' : '선로 여유 (인입 적합)'}
                </h3>
                <p className="text-xs text-slate-600 leading-relaxed font-medium">
                  {kepcoData.isSaturated
                    ? '일반 판매 잔여 0 kW (전문가 우회/선점 검토 필요)'
                    : `${kepcoData.dlName} 잔여 ${kepcoData.dlRemainingKw.toLocaleString()} kW 여유`}
                </p>
              </div>

              {/* Card 2: 20-Year Rent */}
              <div className="bg-white border border-sky-200 bg-sky-50/30 rounded-2xl sm:rounded-3xl p-5 sm:p-6 shadow-md relative overflow-hidden">
                <span className="text-xs font-extrabold text-sky-700 block mb-1">20년 예상 확정 임대수익</span>
                <h3 className="text-xl sm:text-2xl font-black text-sky-600 mb-1.5">
                  최대 {estimatedTotalRentFormatted}
                </h3>
                <p className="text-xs text-slate-600 leading-relaxed font-medium">
                  연간 약 {estimatedAnnualRentFormatted} (설비 약 {estimatedKw} kW 기준 / 선지급 선택 가능)
                </p>
              </div>

              {/* Card 3: Roof Repair Support */}
              <div className="bg-white border border-purple-200 bg-purple-50/30 rounded-2xl sm:rounded-3xl p-5 sm:p-6 shadow-md relative overflow-hidden">
                <span className="text-xs font-extrabold text-purple-700 block mb-1">지붕 수리비 지원 혜택</span>
                <h3 className="text-xl sm:text-2xl font-black text-purple-600 mb-1.5">
                  수리비 전폭 지원
                </h3>
                <p className="text-xs text-slate-600 leading-relaxed font-medium">
                  1등급 전문 방수 시공 & 노후 지붕 보수비용 지원
                </p>
              </div>

            </div>

            {/* ===================== MAP VIEW SECTION (WHITE THEME) ===================== */}
            <div className="bg-white border border-slate-200 rounded-3xl p-5 sm:p-7 shadow-md space-y-4">
              
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-4">
                <div className="flex items-center gap-2.5">
                  <div className="p-2.5 rounded-xl bg-sky-50 text-sky-600 border border-sky-200">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-black text-slate-900 flex items-center gap-2">
                      <span>설치 대상 부지 위성 항공뷰</span>
                      <span className="text-[11px] font-bold text-sky-600 bg-sky-50 px-2 py-0.5 rounded-full border border-sky-200">
                        지붕 정밀 분석
                      </span>
                    </h3>
                    <span className="text-xs text-slate-500 font-medium">
                      {formData.address} {formData.detailAddress}
                    </span>
                  </div>
                </div>

                {/* Satellite / Roadmap Switch & Direct Korean Map Links */}
                <div className="flex flex-wrap items-center gap-2 self-start sm:self-auto">
                  <div className="flex bg-slate-100 p-1 rounded-xl border border-slate-200 text-xs">
                    <button
                      type="button"
                      onClick={() => setMapViewMode('satellite')}
                      className={`px-3 py-1.5 rounded-lg font-bold flex items-center gap-1 cursor-pointer transition-all ${
                        mapViewMode === 'satellite'
                          ? 'bg-white text-slate-900 shadow-sm border border-slate-200'
                          : 'text-slate-500 hover:text-slate-800'
                      }`}
                    >
                      <Layers className="w-3.5 h-3.5 text-sky-600" />
                      <span>위성 영상</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setMapViewMode('roadmap')}
                      className={`px-3 py-1.5 rounded-lg font-bold flex items-center gap-1 cursor-pointer transition-all ${
                        mapViewMode === 'roadmap'
                          ? 'bg-white text-slate-900 shadow-sm border border-slate-200'
                          : 'text-slate-500 hover:text-slate-800'
                      }`}
                    >
                      <MapIcon className="w-3.5 h-3.5 text-sky-600" />
                      <span>일반 지도</span>
                    </button>
                  </div>

                  {/* Direct Kakao Map Pinpoint Link */}
                  <a
                    href={`https://map.kakao.com/link/search/${encodeURIComponent(cleanAddress)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-2 rounded-xl bg-yellow-50 hover:bg-yellow-100 border border-yellow-200 text-yellow-800 font-extrabold text-xs flex items-center gap-1.5 transition-colors shadow-sm"
                  >
                    <span>카카오맵 스카이뷰</span>
                    <ExternalLink className="w-3.5 h-3.5 text-yellow-600" />
                  </a>

                  {/* Direct Naver Map Link */}
                  <a
                    href={`https://map.naver.com/v5/search/${encodeURIComponent(cleanAddress)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-2 rounded-xl bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-800 font-extrabold text-xs flex items-center gap-1.5 transition-colors shadow-sm"
                  >
                    <span>네이버 지도</span>
                    <ExternalLink className="w-3.5 h-3.5 text-emerald-600" />
                  </a>
                </div>
              </div>

              {/* Map Embed Frame */}
              <div className="relative w-full h-80 sm:h-96 rounded-2xl overflow-hidden border border-slate-200 bg-slate-100 shadow-inner">
                <iframe
                  title="설치 대상 부지 지도"
                  src={`https://maps.google.com/maps?q=${encodeURIComponent(
                    cleanAddress
                  )}&t=${mapViewMode === 'satellite' ? 'k' : 'm'}&z=18&ie=UTF8&iwloc=&output=embed`}
                  className="w-full h-full border-0 filter contrast-[1.02]"
                  loading="lazy"
                />

                {/* Floating Map Pin Badge */}
                <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-md border border-slate-200 px-3.5 py-1.5 rounded-xl shadow-md flex items-center gap-2 text-xs">
                  <span className="w-2.5 h-2.5 rounded-full bg-rose-500 animate-ping" />
                  <span className="font-extrabold text-slate-900 tracking-tight">태양광 설치 분석 대상 지붕</span>
                </div>

                {/* Bottom Helper Bar for Kakao Map high precision */}
                <div className="absolute bottom-3 left-3 right-3 bg-white/95 backdrop-blur-md border border-slate-200 p-2.5 rounded-xl shadow-md flex flex-col sm:flex-row items-center justify-between gap-2 text-[11px] text-slate-700">
                  <span className="flex items-center gap-1.5 font-medium">
                    <Compass className="w-3.5 h-3.5 text-sky-600 shrink-0" />
                    <span>실제 지붕 면적과 방위각을 초고화질로 보시려면 우측 상단 <strong>카카오맵 스카이뷰</strong>를 이용해 보세요.</span>
                  </span>
                  <a
                    href={`https://map.kakao.com/link/search/${encodeURIComponent(cleanAddress)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-yellow-700 font-bold hover:underline shrink-0 flex items-center gap-1 bg-yellow-100/60 px-2 py-0.5 rounded"
                  >
                    <span>지붕 크게 보기</span>
                    <ExternalLink className="w-3 h-3 text-yellow-700" />
                  </a>
                </div>
              </div>

            </div>

            {/* Detailed Inspection Box (White Mode) */}
            <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 space-y-6 shadow-md">
              
              <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                <div className="flex items-center gap-2.5">
                  <div className="p-2.5 rounded-xl bg-sky-50 text-sky-600 border border-sky-200">
                    <Cpu className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-black text-slate-900">
                      상세 진단 데이터
                    </h3>
                    <span className="text-xs text-slate-500 font-medium">
                      관할 변전소 및 선로 연계 지표
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs sm:text-sm">
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200/80 flex items-center justify-between">
                  <span className="text-slate-500 font-medium">한전 관할 지사</span>
                  <strong className="text-slate-900 font-bold">{kepcoData.branchName}</strong>
                </div>

                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200/80 flex items-center justify-between">
                  <span className="text-slate-500 font-medium">관할 154kV 변전소</span>
                  <strong className="text-sky-700 font-bold">{kepcoData.substationName}</strong>
                </div>

                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200/80 flex items-center justify-between">
                  <span className="text-slate-500 font-medium">변전소 M.Tr 여유용량</span>
                  <strong className={`font-extrabold ${kepcoData.isSaturated ? 'text-amber-600' : 'text-emerald-600'}`}>
                    {kepcoData.isSaturated ? '0 MW (송전 용량 마감)' : `${kepcoData.substationRemainingMw} MW 여유`}
                  </strong>
                </div>

                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200/80 flex items-center justify-between">
                  <span className="text-slate-500 font-medium">연계 배전선로 (D/L)</span>
                  <strong className="text-sky-700 font-bold">{kepcoData.dlName}</strong>
                </div>

                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200/80 flex items-center justify-between">
                  <span className="text-slate-500 font-medium">선로 일반 잔여용량</span>
                  <strong className={`font-extrabold ${kepcoData.isSaturated ? 'text-amber-600' : 'text-emerald-600'}`}>
                    {kepcoData.isSaturated ? '0 kW (계통 포화 상태)' : `${kepcoData.dlRemainingKw.toLocaleString()} kW 여유`}
                  </strong>
                </div>

                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200/80 flex items-center justify-between">
                  <span className="text-slate-500 font-medium">선로 인입 전압 방식</span>
                  <strong className="text-slate-900 font-bold">{kepcoData.voltageType}</strong>
                </div>

                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200/80 flex items-center justify-between">
                  <span className="text-slate-500 font-medium">설치 대상 부지 면적</span>
                  <strong className="text-slate-900 font-bold">{formData.pyeong}평 ({areaSqm.toLocaleString()} ㎡)</strong>
                </div>

                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200/80 flex items-center justify-between">
                  <span className="text-slate-500 font-medium">예상 태양광 설비 용량</span>
                  <strong className="text-sky-600 font-black text-sm">약 {estimatedKw} kW</strong>
                </div>
              </div>

              {/* Realistic Engineering Solution Notice Box */}
              <div className={`p-4 sm:p-5 rounded-2xl border text-xs sm:text-sm space-y-2.5 ${
                kepcoData.isSaturated
                  ? 'bg-amber-50 border-amber-200 text-amber-900'
                  : 'bg-sky-50 border-sky-200 text-slate-800'
              }`}>
                <div className="flex items-center gap-2 font-extrabold">
                  {kepcoData.isSaturated ? (
                    <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                  ) : (
                    <ShieldCheck className="w-4 h-4 text-sky-600 shrink-0" />
                  )}
                  <span className={kepcoData.isSaturated ? 'text-amber-700' : 'text-sky-700'}>
                    {kepcoData.isSaturated
                      ? '전문 엔지니어 현장 우회 인입 및 선로 선점 대기 진단 필수 권역'
                      : '한전 계통 선로 용량 선점 및 1:1 담당 엔지니어 배정 완료'}
                  </span>
                </div>

                <p className="leading-relaxed font-medium keep-all text-slate-700">
                  {kepcoData.isSaturated ? (
                    <>
                      해당 주소지는 한전 전산상 일반 송전용량이 마감(0kW)된 지역입니다. 지붕임대 사업은 전력 판매가 핵심이므로, 
                      <strong>플러스에스디 1등급 EPC 전문 기술팀</strong>이 <strong>① 인근 여유 선로 우회 인입 타당성 검토</strong> 및 
                      <strong>② 한전 선로 취소분 선착순 선점 대기 신청</strong>을 무상으로 정밀 진단해 드립니다.
                    </>
                  ) : (
                    <>
                      대표님께서 신청하신 <strong>{formData.address}</strong>의 계통 분석 데이터가 플러스에스디 전담 엔지니어 팀에 접수되었습니다. 
                      한전 전산의 실시간 변전소·배전선로 도면을 최종 대조한 후, 입력해주신 연락처(<strong>{formData.phone}</strong>)와 이메일(<strong>{formData.email}</strong>)로 1:1 맞춤 선로 선점 분석표를 보내드립니다.
                    </>
                  )}
                </p>
              </div>

            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              
              {/* KEPCO ON External Direct Link */}
              <a
                href="https://online.kepco.co.kr"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 py-4 px-5 rounded-2xl bg-white hover:bg-slate-100 border border-slate-300 text-slate-800 font-extrabold text-xs sm:text-sm transition-all flex items-center justify-center gap-2 shadow-sm"
              >
                <span>한국전력공사(한전 ON) 공식 확인</span>
                <ExternalLink className="w-4 h-4 text-sky-600" />
              </a>

              {/* Direct Call to Donghyun */}
              <a
                href="tel:01027671449"
                className="flex-1 py-4 px-5 rounded-2xl bg-sky-500 hover:bg-sky-600 text-white font-black text-xs sm:text-sm transition-all flex items-center justify-center gap-2 shadow-lg shadow-sky-500/25 active:scale-95"
              >
                <Phone className="w-4 h-4" />
                <span>010-2767-1449 전담 엔지니어 직통 상담</span>
              </a>

            </div>

            <div className="text-center pt-4">
              <button
                onClick={handleReset}
                className="inline-flex items-center gap-2 text-xs text-slate-500 hover:text-slate-800 underline cursor-pointer font-medium"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                <span>다른 주소로 다시 계산하기</span>
              </button>
            </div>

          </div>
        ) : (
          /* ===================== VIEW 2: INPUT FORM (CLEAN WHITE MODE) ===================== */
          <div className="space-y-8 animate-in fade-in duration-300">
            
            {/* Page Title & Intro */}
            <div className="text-center space-y-3">
              <div className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-sky-50 border border-sky-200 text-sky-600 text-xs font-extrabold tracking-wider uppercase shadow-sm">
                <Sparkles className="w-3.5 h-3.5" />
                <span>실시간 분석</span>
              </div>

              <h1 className="text-3xl sm:text-5xl font-black text-slate-900 tracking-tight leading-tight keep-all">
                우리 지붕 <br className="sm:hidden" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-blue-600">
                  임대수익 가능할까요?
                </span>
              </h1>

              <p className="text-slate-600 text-xs sm:text-base max-w-2xl mx-auto font-medium leading-relaxed keep-all">
                지금 바로 실시간으로 분석하세요
              </p>
            </div>

            {/* Interactive Form Card (White Card) */}
            <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-10 shadow-xl">
              <form onSubmit={handleSubmit} className="space-y-6">
                
                {/* 1. Address Search (Mandatory Search Popup) */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="block text-xs sm:text-sm font-extrabold text-slate-800">
                      설치 주소 <span className="text-rose-500">*</span>
                    </label>
                    <span className="text-[11px] text-sky-600 font-bold">
                      * 주소 검색 필수
                    </span>
                  </div>

                  {/* Readonly Address Trigger Box */}
                  <div
                    onClick={handleOpenAddressSearch}
                    className="flex items-center gap-2 p-1.5 rounded-2xl bg-slate-50 border border-slate-200 hover:border-sky-500 transition-colors cursor-pointer group shadow-sm"
                  >
                    <div className="p-2.5 rounded-xl bg-white text-sky-600 border border-slate-200 group-hover:bg-sky-500 group-hover:text-white transition-colors shrink-0 ml-1 shadow-sm">
                      <MapPin className="w-5 h-5" />
                    </div>
                    
                    <input
                      type="text"
                      readOnly
                      placeholder="클릭하여 주소를 검색하세요 (도로명 또는 지번)"
                      value={formData.address ? `${formData.address} (${formData.zonecode || ''})` : ''}
                      className="w-full py-2 bg-transparent text-slate-900 placeholder-slate-400 text-xs sm:text-sm cursor-pointer focus:outline-none font-medium"
                    />

                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenAddressSearch();
                      }}
                      className="px-5 py-3 rounded-xl bg-sky-500 hover:bg-sky-600 text-white font-extrabold text-xs sm:text-sm transition-all shadow-md shadow-sky-500/20 active:scale-95 cursor-pointer flex items-center gap-1.5 shrink-0 whitespace-nowrap mr-1"
                    >
                      <Search className="w-4 h-4" />
                      <span>{formData.address ? '주소 재검색' : '주소 검색'}</span>
                    </button>
                  </div>

                  {/* Selected Address Display & Detail Input */}
                  {formData.address && (
                    <div className="space-y-2 pt-1 animate-in fade-in">
                      <input
                        type="text"
                        placeholder="상세 주소를 입력하세요 (예: 1동 지붕, 가동 공장 등)"
                        value={formData.detailAddress}
                        onChange={(e) => setFormData({ ...formData, detailAddress: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400 text-xs sm:text-sm focus:border-sky-500 focus:bg-white focus:outline-none transition-colors"
                      />
                      <p className="text-[11px] text-emerald-600 font-bold flex items-center gap-1.5">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        <span>선택된 주소: {formData.address}</span>
                      </p>
                    </div>
                  )}
                </div>

                {/* 2. Pyeong / Area Size */}
                <div className="space-y-2.5">
                  <div className="flex items-center justify-between">
                    <label className="text-xs sm:text-sm font-extrabold text-slate-800">
                      평수 <span className="text-rose-500">*</span>
                    </label>
                    <span className="text-xs text-sky-600 font-bold">
                      약 {areaSqm.toLocaleString()} ㎡
                    </span>
                  </div>

                  <div className="flex gap-2">
                    <input
                      type="number"
                      min={10}
                      max={100000}
                      value={formData.pyeong}
                      onChange={(e) => setFormData({ ...formData, pyeong: Number(e.target.value) })}
                      className="w-full px-4 py-3.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 text-base font-black focus:border-sky-500 focus:bg-white focus:outline-none transition-colors"
                      placeholder="예: 200"
                    />
                    <span className="flex items-center px-4 rounded-xl bg-slate-100 border border-slate-200 text-slate-700 font-bold text-sm">
                      평
                    </span>
                  </div>

                  {/* Preset Quick Click Chips */}
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5 sm:gap-2 pt-1">
                    {pyeongPresets.map((pz) => (
                      <button
                        key={pz}
                        type="button"
                        onClick={() => setFormData({ ...formData, pyeong: pz })}
                        className={`py-2 px-2 rounded-xl text-xs font-bold border transition-all cursor-pointer ${
                          formData.pyeong === pz
                            ? 'bg-sky-500 text-white border-sky-500 font-black shadow-sm'
                            : 'bg-slate-50 text-slate-700 border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        {pz >= 1000 ? `${pz / 1000}천평` : `${pz}평`}
                      </button>
                    ))}
                  </div>
                </div>

                {/* 3. Building / Land Purpose */}
                <div className="space-y-2">
                  <label className="block text-xs sm:text-sm font-extrabold text-slate-800">
                    건물 및 부지 용도 <span className="text-rose-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {buildingUseOptions.map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => setFormData({ ...formData, buildingUse: opt })}
                        className={`py-3 px-3 rounded-xl text-xs font-bold border transition-all cursor-pointer flex items-center justify-center gap-1.5 ${
                          formData.buildingUse === opt
                            ? 'bg-sky-50 text-sky-700 border-sky-400 shadow-sm font-extrabold'
                            : 'bg-slate-50 text-slate-700 border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <Building2 className="w-3.5 h-3.5" />
                        <span>{opt}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* 4. Customer Name & Phone */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-extrabold text-slate-800 mb-1.5">
                      성함 / 업체명 <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        required
                        placeholder="예: 홍길동 (또는 OO공업)"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-4 py-3.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400 text-xs sm:text-sm focus:border-sky-500 focus:bg-white focus:outline-none transition-colors font-medium"
                      />
                      <User className="w-4 h-4 text-slate-400 absolute right-4 top-1/2 -translate-y-1/2" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-extrabold text-slate-800 mb-1.5">
                      연락처 <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="tel"
                        required
                        maxLength={13}
                        placeholder="010-0000-0000"
                        value={formData.phone}
                        onChange={handlePhoneChange}
                        className="w-full px-4 py-3.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400 text-xs sm:text-sm focus:border-sky-500 focus:bg-white focus:outline-none transition-colors font-medium"
                      />
                      <Phone className="w-4 h-4 text-slate-400 absolute right-4 top-1/2 -translate-y-1/2" />
                    </div>
                  </div>
                </div>

                {/* 5. Email for Report Receipt */}
                <div>
                  <label className="block text-xs sm:text-sm font-extrabold text-slate-800 mb-1.5">
                    분석 리포트 수신 이메일 <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      required
                      placeholder="report@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400 text-xs sm:text-sm focus:border-sky-500 focus:bg-white focus:outline-none transition-colors font-medium"
                    />
                    <Mail className="w-4 h-4 text-slate-400 absolute right-4 top-1/2 -translate-y-1/2" />
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1 font-medium">
                    신청해 주시면 전담 엔지니어가 계통 검토 후 입력하신 연락처와 이메일로 맞춤 분석표를 발송해 드립니다.
                  </p>
                </div>

                {/* 6. Agreements */}
                <div className="space-y-2.5 pt-2 border-t border-slate-100">
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 text-xs text-slate-700 font-semibold cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.privacyAgreed}
                        onChange={(e) => setFormData({ ...formData, privacyAgreed: e.target.checked })}
                        className="w-4 h-4 rounded bg-slate-50 border-slate-300 text-sky-500 focus:ring-sky-500 cursor-pointer"
                      />
                      <span>[필수] 개인정보 수집 및 이용에 동의합니다.</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowPrivacyModal(true)}
                      className="text-[11px] text-slate-400 hover:text-sky-600 underline cursor-pointer"
                    >
                      약관 보기
                    </button>
                  </div>

                  <div className="flex items-center">
                    <label className="flex items-center gap-2 text-xs text-slate-700 font-semibold cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.marketingAgreed}
                        onChange={(e) => setFormData({ ...formData, marketingAgreed: e.target.checked })}
                        className="w-4 h-4 rounded bg-slate-50 border-slate-300 text-sky-500 focus:ring-sky-500 cursor-pointer"
                      />
                      <span>[필수] 개인정보 활용 및 상담수신에 동의합니다.</span>
                    </label>
                  </div>
                </div>

                {/* Submit Action Button */}
                <button
                  type="submit"
                  disabled={analyzing}
                  className="w-full py-4.5 rounded-2xl bg-sky-500 hover:bg-sky-600 text-white font-black text-sm sm:text-base transition-all shadow-xl shadow-sky-500/25 active:scale-95 cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <Zap className="w-5 h-5 text-white" />
                  <span>실시간 분석하기</span>
                  <ArrowRight className="w-4 h-4" />
                </button>

              </form>
            </div>

            {/* Bottom Safe Badge */}
            <div className="flex items-center justify-center gap-4 text-xs text-slate-500 font-medium">
              <div className="flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-emerald-600" />
                <span>한전 배전계통 실시간 데이터 연동</span>
              </div>
              <span>•</span>
              <div className="flex items-center gap-1.5">
                <FileText className="w-4 h-4 text-sky-600" />
                <span>100% 무료 진단 및 대외비 보장</span>
              </div>
            </div>

          </div>
        )}

      </main>

      {/* Privacy Terms Modal (White Mode) */}
      {showPrivacyModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in"
          onClick={() => setShowPrivacyModal(false)}
        >
          <div
            className="relative w-full max-w-lg bg-white border border-slate-200 rounded-3xl p-6 shadow-2xl text-slate-700 text-xs leading-relaxed space-y-4 max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <h4 className="text-base font-black text-slate-900">개인정보 수집 및 이용 동의</h4>
              <button
                onClick={() => setShowPrivacyModal(false)}
                className="text-slate-400 hover:text-slate-700 p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3 text-slate-600">
              <p>
                <strong>1. 수집 항목:</strong> 성함/업체명, 연락처, 이메일, 설치 대상 주소, 평수, 건물 용도
              </p>
              <p>
                <strong>2. 수집 및 이용 목적:</strong> 한전 배전선로 여유용량 분석, 태양광 임대 수익 및 지붕 수리비 지원 산정, 1:1 상담 안내 및 분석 리포트 발송
              </p>
              <p>
                <strong>3. 보유 및 이용 기간:</strong> 상담 목적 달성 후 1년간 보관 또는 정보주체의 삭제 요청 시 즉시 파기
              </p>
              <p className="text-slate-400">
                * 귀하는 개인정보 수집 및 이용에 대한 동의를 거부할 권리가 있으나, 거부 시 실시간 분석 서비스 이용이 제한될 수 있습니다.
              </p>
            </div>

            <button
              onClick={() => setShowPrivacyModal(false)}
              className="w-full py-3 rounded-xl bg-sky-500 hover:bg-sky-600 text-white font-bold cursor-pointer"
            >
              확인
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
